/**
 * Admin → Entwicklereinstellungen → Mail-Versand.
 *
 * Lists the calling admin's mail templates (which they author in
 * /settings/mail-templates with the existing block builder) and lets
 * them broadcast any one of them to:
 *   - Test-Empfänger    (single send to admin's email)
 *   - Anfänger-Modus aktiv
 *   - Alle Benutzer
 *
 * The category field on each template is used to label which class of
 * mail it is (Rechnung / Erinnerung / Info / Werbung).
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Pencil, Send } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  useBroadcastMail,
  useMyMailTemplates,
} from '../../hooks/useInvoices';
import { useUser } from '../../auth/AuthProvider';

const CATEGORY_LABEL: Record<string, string> = {
  invoice: 'Rechnung',
  reminder: 'Erinnerung',
  info: 'Info',
  marketing: 'Werbung',
  transactional: 'Transaktional',
  broadcast: 'Broadcast',
};

const KNOWN_CATEGORIES = ['invoice', 'reminder', 'info', 'marketing'];

export function AdminMailBroadcastPage() {
  const templates = useMyMailTemplates();
  const [filter, setFilter] = React.useState<string>('all');
  const [broadcastTarget, setBroadcastTarget] = React.useState<{
    id: string;
    name: string;
    subject: string;
  } | null>(null);

  const items = templates.data?.items ?? [];
  const filtered =
    filter === 'all' ? items : items.filter((t) => (t.category ?? '') === filter);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Mail-Vorlagen &amp; Versand</CardTitle>
          <CardDescription>
            Diese Liste zeigt deine eigenen Mail-Vorlagen aus dem{' '}
            <Link to="/settings/mail-templates" className="text-primary hover:underline">
              regulären Mail-Builder
            </Link>
            . Jede kannst du an einen ausgewählten Empfängerkreis broadcasten —
            Rechnungs-, Erinnerungs-, Info- oder Werbe-Mails kategorisierst du
            durch das <code>category</code>-Feld der Vorlage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs">Filter:</Label>
            {(['all', ...KNOWN_CATEGORIES] as const).map((c) => (
              <Button
                key={c}
                size="sm"
                variant={filter === c ? 'default' : 'outline'}
                onClick={() => setFilter(c)}
              >
                {c === 'all' ? 'Alle' : CATEGORY_LABEL[c] ?? c}
              </Button>
            ))}
            <div className="flex-1" />
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/mail-templates">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Vorlagen verwalten
                <ExternalLink className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vorlagen</CardTitle>
          <CardDescription>
            {templates.isLoading ? 'Lade …' : `${filtered.length} Vorlagen`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Keine Vorlagen in dieser Kategorie. Lege im{' '}
              <Link to="/settings/mail-templates" className="text-primary hover:underline">
                Mail-Builder
              </Link>{' '}
              eine an und setze das Feld <code>category</code>.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Betreff</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.subject ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {CATEGORY_LABEL[t.category ?? ''] ?? t.category ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          asChild
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          title="Bearbeiten"
                        >
                          <Link to={`/settings/mail-templates/${t.id}`}>
                            <Pencil className="h-3 w-3" />
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          className="h-7 text-xs"
                          onClick={() =>
                            setBroadcastTarget({
                              id: t.id,
                              name: t.name,
                              subject: t.subject ?? '',
                            })
                          }
                        >
                          <Send className="mr-1 h-3 w-3" /> Versenden
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {broadcastTarget && (
        <BroadcastDialog
          template={broadcastTarget}
          onClose={() => setBroadcastTarget(null)}
        />
      )}
    </div>
  );
}

function BroadcastDialog({
  template,
  onClose,
}: {
  template: { id: string; name: string; subject: string };
  onClose: () => void;
}) {
  const { user } = useUser();
  const broadcast = useBroadcastMail();
  const { toast } = useToast();
  const [audience, setAudience] = React.useState<
    'test_only' | 'all_active_beginner' | 'all_users'
  >('test_only');
  const [testEmail, setTestEmail] = React.useState(user?.email ?? '');
  const [subjectOverride, setSubjectOverride] = React.useState('');

  async function onSend() {
    if (audience !== 'test_only') {
      const sure =
        audience === 'all_users'
          ? confirm('Wirklich an ALLE registrierten Benutzer senden?')
          : confirm('Wirklich an alle Anfänger-Modus-Kunden senden?');
      if (!sure) return;
    }
    try {
      const r = await broadcast.mutateAsync({
        templateOwnerUid: user!.uid,
        templateId: template.id,
        audience,
        testEmail: audience === 'test_only' ? testEmail.trim() : undefined,
        subjectOverride: subjectOverride.trim() || undefined,
      });
      toast({
        title: `Versand abgeschlossen`,
        description: `${r.sent} verschickt, ${r.errors} Fehler.`,
      });
      onClose();
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vorlage versenden</DialogTitle>
          <DialogDescription>
            Vorlage: <strong>{template.name}</strong>
            <br />
            Default-Betreff: <code>{template.subject}</code>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Empfängerkreis</Label>
            <Select value={audience} onValueChange={(v) => setAudience(v as typeof audience)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test_only">Nur Testempfänger</SelectItem>
                <SelectItem value="all_active_beginner">
                  Alle aktiven Anfänger-Modus-Kunden
                </SelectItem>
                <SelectItem value="all_users">Alle registrierten Benutzer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {audience === 'test_only' && (
            <div className="space-y-1.5">
              <Label>Test-E-Mail</Label>
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="dev@meinefirma.at"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Betreff überschreiben (optional)</Label>
            <Input
              value={subjectOverride}
              onChange={(e) => setSubjectOverride(e.target.value)}
              placeholder={`Standard: ${template.subject}`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => void onSend()} disabled={broadcast.isPending}>
            {broadcast.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Versenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
